
import { computed, defineComponent, onMounted, ref } from 'vue';

import { onClickOutside, useElementBounding, useDebounceFn } from '@vueuse/core';
import { useStore } from 'vuex';

export default defineComponent({
    name: "EasyDrop",
    emits: ['opened', 'isLockedFunction'],
    props: {
        position: {
            type: String,
            default: 'left'
        },
        customClasses: {
            type: String,
            required: false,
        },
        customButtonClasses: {
            type: String,
            required: false
        },
        isFullMobile: {
            type: Boolean
        },
        hasTopTip: {
            type: Boolean
        },
        dropDown: {
            type: Boolean
        },
        dropdownType: {
            type: String,
            default: "dropdown"
        },
        selectButtonValue: {
            type: String,
            required: false
        },
        useLabel: {
            type: Boolean
        },
        isAccordion: {
            type: Boolean
        },
        buttonId: {
            type: String,
        },
        isLocked: {
            type: Boolean
        }
    },
    setup(_props, { slots, emit }) {
        const store = useStore();
        const hasSlot = name => !!slots[name];

        const pageTexts = computed(() => store.getters["ApplicationModule/getPageTexts"]);

        const dropdown = ref(_props.dropDown);
        const dropdownRef = ref();
        const dropdownButton = ref();
        const dropdownActive = ref(false);
        const openTop = ref(false);

        const positionValue = ref(0);
        const transitionInProgress = ref(false);

        const positionY = (triggerX = true) => {
            openTop.value = false;
            if (!_props.isAccordion) {
                setTimeout(() => {
                    const content = dropdownRef.value as HTMLDivElement;
                    if (content) {
                        const wdwHeight = window.innerHeight;
                        const elRect = content.getBoundingClientRect();
                        const elBottomPosition = content.clientHeight + elRect.top;

                        if (elBottomPosition > wdwHeight && elRect.top > content.clientHeight)
                            openTop.value = true;
                    }
                }, 0)
            }

            if (triggerX) positionX();
        }

        const positionX = () => {
            if (_props.dropdownType === "dropdown" || (_props.dropdownType === 'tooltip' && _props.hasTopTip)) {
                const { right, left, width } = useElementBounding(dropdownRef);
                const wdwWidth = document.body.offsetWidth - 15;
                const dropButton = dropdownButton.value as HTMLButtonElement;
                if (_props.position === "center") {
                    positionValue.value = -Math.abs((width.value - dropButton.offsetWidth) / 2);
                    setTimeout(() => {
                        if (right.value > wdwWidth) {
                            positionValue.value = wdwWidth - (right.value) + positionValue.value;
                        } else if (left.value + positionValue.value < 0) {
                            positionValue.value = -Math.abs(left.value - positionValue.value) + 15;
                        }
                    }, 0)
                } else {
                    positionValue.value = 0;
                    setTimeout(() => {
                        if (right.value > wdwWidth) {
                            positionValue.value = wdwWidth - right.value;
                        } else if (left.value < 0) {
                            positionValue.value = _props.position === "right" ? -Math.abs(left.value) - 15 : Math.abs(left.value) + 15;
                        }

                    }, 0)
                }
            }

        }

        const scrollPositioning = useDebounceFn(() => {
            positionY(false);
        }, 200);

        const resizePosition = useDebounceFn(() => {
            positionY();
        }, 200);

        const toggleDropdown = () => {
            if (!transitionInProgress.value) {
                transitionInProgress.value = true;

                if (_props.isLocked) {
                    dropdown.value = true;
                    emit('isLockedFunction');
                }
                else
                    dropdown.value = !dropdown.value;

                if (dropdown.value) {
                    dropdownActive.value = true;
                    positionY();
                    window.addEventListener('resize', resizePosition);
                    window.addEventListener('scroll', scrollPositioning, { passive: true });
                    if (_props.isFullMobile)
                        document.body.classList.add('full-screen-mobile-overlay');
                } else {
                    setTimeout(() => {
                        dropdownActive.value = false;
                    }, 100);
                    setTimeout(() => {
                        openTop.value = false;
                    }, 300);
                    window.removeEventListener('resize', resizePosition);
                    window.removeEventListener('scroll', scrollPositioning);
                    document.body.classList.remove('full-screen-mobile-overlay');
                }
                setTimeout(() => {
                    transitionInProgress.value = false;
                }, 300);
            }
        }

        onClickOutside(
            dropdownRef,
            (event) => {
                if (_props.isLocked) {
                    emit('isLockedFunction');
                } else if (event.target !== dropdownButton.value) {
                    dropdown.value = false;
                    window.removeEventListener('resize', resizePosition);
                    setTimeout(() => {
                        dropdownActive.value = false;
                    }, 100);
                }
            },
        );

        const closeDropdown = (event: Event) => {
            const pointerEvent = event as PointerEvent;
            // clientX and clientY === 0 means it is triggered by keyboard and should not close select
            if (pointerEvent.clientX !== 0 && pointerEvent.clientY !== 0)
                dropdown.value = false;
        }

        onMounted(() => {
            if (_props.dropdownType === 'select' && dropdownRef.value) {
                const selectElement = dropdownRef.value as HTMLDivElement;
                const radios = selectElement.querySelectorAll('input[type=radio]');
                if (radios) {
                    for (const radio of radios) {
                        radio.addEventListener('click', closeDropdown);
                    }
                }
            }
        })

        return {
            dropdown,
            dropdownRef,
            dropdownButton,
            toggleDropdown,
            positionValue,
            hasSlot,
            dropdownActive,
            openTop,
            pageTexts
        }
    },
})
